import React, { useEffect, useMemo, useState } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import ContentSection from '../../../components/layout/ContentSection';
import { fetchObjectTypeCombinations } from './api/objectTypeCombinationsApi';

const CombinationRow = ({ combo, count }) => (
  <tr className="border-b last:border-0">
    <td className="py-2 pr-4 text-gray-800 truncate" title={combo.join(' + ')}>
      {combo.length > 0 ? combo.join(' + ') : '—'}
    </td>
    <td className="py-2 pr-2 text-right font-medium text-gray-900">
      {typeof count === 'number' ? count.toLocaleString() : '—'}
    </td>
  </tr>
);

const useObjectTypeCombinations = (activeFile = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (fileType) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchObjectTypeCombinations(fileType ?? activeFile ?? null);
      setData(res || null);
    } catch (e) {
      const msg = String(e?.message || e || '');
      if (/Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg)) {
        setData({ file_type: fileType || activeFile || 'main', items: [], validation: { events_processed: 0, events_with_no_objects: 0, referenced_object_ids: 0, unknown_object_ids: 0, consistency_ok: true, note: 'Endpoint not available' } });
        setError(null);
      } else {
        setError(msg || 'Failed to load object type combinations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchObjectTypeCombinations(activeFile ?? null);
        if (!cancelled) setData(res || null);
      } catch (e) {
        if (!cancelled) {
          const msg = String(e?.message || e || '');
          if (/Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg)) {
            setData({ file_type: activeFile || 'main', items: [], validation: { events_processed: 0, events_with_no_objects: 0, referenced_object_ids: 0, unknown_object_ids: 0, consistency_ok: true, note: 'Endpoint not available' } });
            setError(null);
          } else {
            setError(msg || 'Failed to load object type combinations');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeFile]);

  return { data, loading, error, refresh: load };
};

const ObjectTypeCombinations = ({ activeFile, topN = 15 }) => {
  const { data, loading, error } = useObjectTypeCombinations(activeFile);

  const items = useMemo(() => {
    const arr = Array.isArray(data?.items) ? [...data.items] : [];
    // Already sorted by backend: count desc, length desc, then lexicographically
    if (arr.length <= topN + 2) return arr;
    return arr.slice(0, topN);
  }, [data, topN]);

  const totalCombos = useMemo(() => items.reduce((acc, it) => acc + (it?.count || 0), 0), [items]);
  const validation = data?.validation;

  return (
    <ContentSection spacing="lg">
      <BaseTile
        title="Object Type Combinations per Event"
        subtitle={totalCombos > 0 ? `Top combinations • Events matched: ${totalCombos.toLocaleString()}` : undefined}
        loading={loading}
        error={error}
        minHeight="lg"
      >
        <div className="p-4">
          {items.length === 0 && !loading && !error ? (
            <div className="text-sm text-gray-600 text-center py-6">No combinations found for the current dataset.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Object Type Combination</th>
                    <th className="py-2 pr-2 text-right">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <CombinationRow key={`${it.combination?.join('|')}-${idx}`} combo={it.combination || []} count={it.count} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {validation && (
            <div className="mt-3 text-xs text-gray-500 space-y-1">
              <div>Events processed: {Number(validation.events_processed || 0).toLocaleString()}</div>
              <div>Events with no objects: {Number(validation.events_with_no_objects || 0).toLocaleString()}</div>
              {typeof validation.unknown_object_ids === 'number' && (
                <div>Unknown object IDs in relations: {Number(validation.unknown_object_ids).toLocaleString()}</div>
              )}
              {typeof validation.consistency_ok === 'boolean' && (
                <div>Validation: {validation.consistency_ok ? 'OK' : 'Issues detected'}</div>
              )}
            </div>
          )}
        </div>
      </BaseTile>
    </ContentSection>
  );
};

export default ObjectTypeCombinations;
